import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { AuthService } from '../auth/auth.service';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable()
export class ConnectionService {

    private getHttpHeadders(pContentType: string): HttpHeaders {
        if (environment.userSecret && environment.x3scaleProxySecretToken && environment.xClkToken) {
            return new HttpHeaders({
                'Content-type': pContentType,
                'user-secret': environment.userSecret,
                'X-3scale-proxy-secret-token': environment.x3scaleProxySecretToken,
                'X-clk-token': environment.xClkToken,
                'X-application-source': environment.xApplicationSource
            });
        } else {
            return new HttpHeaders({
                'Content-type': pContentType,
                'X-application-source': environment.xApplicationSource
            });
        }
    }

    constructor(private httpClient: HttpClient, private authService: AuthService) {
    }

    postRequest(pUrl: string, pPostData: any, pContentType: string, pParams?: string) {
        let url = pUrl;

        if (pParams != null && pParams !== undefined) {
            url = url + '?' + pParams;
        }

        const postData = pPostData;
        const httpOptions = {
            headers: this.getHttpHeadders(pContentType),
            params: new HttpParams().set('access_token', this.authService.getAccessToken())
        };
        return this.httpClient.post(url, postData, httpOptions);
    }

    postCustomRequest(pUrl: string, pPostData: any, pContentType: string, pParams?: string, pTokenType?: string, pToken?: string) {
        let url = pUrl;
        let httpOptionsParams: HttpParams;

        if (pTokenType) {
            if (pToken) {
                httpOptionsParams = new HttpParams().set(pTokenType, pToken);
            } else {
                httpOptionsParams = new HttpParams().set(pTokenType, this.authService.getAccessToken());
            }
        } else {
            httpOptionsParams = new HttpParams().set('access_token', this.authService.getAccessToken());
        }

        if (pParams != null && pParams !== undefined) {
            url = url + '?' + pParams;
        }

        const postData = pPostData;
        const httpOptions = {
            headers: this.getHttpHeadders(pContentType),
            params: httpOptionsParams
        };
        return this.httpClient.post(url, postData, httpOptions);
    }

    postRequestWithToken(pUrl: string, pPostData: any, pContentType: string, token: string, pParams?: string) {
        let url = pUrl;

        if (pParams != null && pParams !== undefined) {
            url = url + '?' + pParams;
        }

        const postData = pPostData;
        const httpOptions = {
            headers: this.getHttpHeadders(pContentType),
            params: new HttpParams().set('token', token)
        };
        return this.httpClient.post(url, postData, httpOptions);
    }

    postRequestWithAccessToken(pUrl: string, pPostData: any, pContentType: string, token: string, pParams?: string) {
        let url = pUrl;

        if (pParams != null && pParams !== undefined) {
            url = url + '?' + pParams;
        }

        const postData = pPostData;
        const httpOptions = {
            headers: this.getHttpHeadders(pContentType),
            params: new HttpParams().set('access_token', token)
        };
        return this.httpClient.post(url, postData, httpOptions);
    }

    getRequest(pUrl: string, pContentType: string, pParams?: string, pOptions?: any) {
        let url = pUrl;
        let params: HttpParams;

        if (pParams != null && pParams !== undefined) {
            url = url + '?' + pParams;
        }

        if (pOptions != null && pOptions !== undefined) {
            params = new MyHttpParams({
                interceptorMetadata: { options: pOptions }
            }).set('access_token', this.authService.getAccessToken());
        } else {
            params = new HttpParams().set('access_token', this.authService.getAccessToken());
        }

        const httpOptions = {
            headers: this.getHttpHeadders(pContentType),
            params: params
        };

        return this.httpClient.get(url, httpOptions);
    }

    getCustomRequest(pUrl: string, pContentType: string, pParams?: string, pTokenType?: string, pToken?: string) {
        let url = pUrl;
        let httpOptionsParams: HttpParams;

        if (pParams != null && pParams !== undefined) {
            url = url + '?' + pParams;
        }

        if (pTokenType) {
            if (pToken) {
                httpOptionsParams = new HttpParams().set(pTokenType, pToken);
            } else {
                httpOptionsParams = new HttpParams().set(pTokenType, this.authService.getAccessToken());
            }
        } else {
            httpOptionsParams = new HttpParams().set('access_token', this.authService.getAccessToken());
        }

        const httpOptions = {
            headers: this.getHttpHeadders(pContentType),
            params: httpOptionsParams
        };

        return this.httpClient.get(url, httpOptions);
    }

    getRequestWithToken(pUrl: string, pContentType: string, token: string, pParams?: string) {
        let url = pUrl;

        if (pParams != null && pParams !== undefined) {
            url = url + '?' + pParams;
        }

        const httpOptions = {
            headers: this.getHttpHeadders(pContentType),
            params: new HttpParams().set('token', token)
        };

        return this.httpClient.get(url, httpOptions);
    }

    getRequestWithAccessToken(pUrl: string, pContentType: string, token: string, pParams?: string) {
        let url = pUrl;

        if (pParams != null && pParams !== undefined) {
            url = url + '?' + pParams;
        }

        const httpOptions = {
            headers: this.getHttpHeadders(pContentType),
            params: new HttpParams().set('access_token', token)
        };

        return this.httpClient.get(url, httpOptions);
    }

    deleteRequest(pUrl: string, pContentType: string) {
        const url = pUrl;
        const httpOptions = {
            headers: this.getHttpHeadders(pContentType),
            params: new HttpParams().set('access_token', this.authService.getAccessToken())
        };
        return this.httpClient.delete(url, httpOptions);
    }

    postRequestUnsecured(pUrl: string, pPostData: any, pContentType: string) {
        const url = pUrl;
        const postData = pPostData;
        const httpOptions = { headers: this.getHttpHeadders(pContentType) };

        return new Promise((resolve, reject) => {
            this.httpClient.post(url, postData, httpOptions).pipe(tap(
                data => resolve(data),
                error => resolve(error)
            )).subscribe();
        });
    }

    getRequestUnsecured(pUrl: string, pContentType: string) {
        const url = pUrl;
        const httpOptions = { headers: this.getHttpHeadders(pContentType) };
        return this.httpClient.get(url, httpOptions);
    }
}

export interface InterceptorMetadata {
    readonly options: any;
}

export interface MyHttpParamsConfig {
    interceptorMetadata: Partial<InterceptorMetadata>;
    params: string | Record<string, string | string[]>; // the actual params which will be included in the real request
}

export class MyHttpParams extends HttpParams {
    public readonly interceptorMetadata: Partial<InterceptorMetadata>;

    constructor({ params, interceptorMetadata }: Partial<MyHttpParamsConfig> = {}) {
        if (typeof params === 'string') {
            super({ fromString: params });
        }
        else if (typeof params === 'object') {
            super({ fromObject: params });
        }
        else {
            super();
        }

        this.interceptorMetadata = interceptorMetadata;
    }

    // orverrides HttpParams.append
    append(param: string, value: string): MyHttpParams {
        const updatedHttpParams = super.append(param, value);
        return new MyHttpParams({
            interceptorMetadata: this.interceptorMetadata,
            params: updatedHttpParams.toString()
        });
    }

    // orverrides HttpParams.set
    set(param: string, value: string): MyHttpParams {
        const updatedHttpParams = super.set(param, value);
        return new MyHttpParams({
            interceptorMetadata: this.interceptorMetadata,
            params: updatedHttpParams.toString()
        });
    }

    // orverrides HttpParams.delete
    delete(param: string, value?: string): MyHttpParams {
        const updatedHttpParams = super.delete(param, value);
        return new MyHttpParams({
            interceptorMetadata: this.interceptorMetadata,
            params: updatedHttpParams.toString()
        });
    }
}